/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import * as Popper from "@radix-ui/react-popper";
import type {
  FormEventHandler,
  KeyboardEvent,
  KeyboardEventHandler,
  PropsWithChildren,
  ReactNode,
  RefObject,
} from "react";
import { createRef, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import type { ThemeType } from "../../../../../global/emotion";
import {
  fontSizes,
  lineHeights,
  numericPercentages,
  numericViewportHeight,
  numericViewportWidth,
  radius,
  spacing,
  stroke,
  useTheme,
  zIndices,
} from "../../../../../global/emotion";
import { END_OF_BROWSER_ASYNC_QUEUE } from "../../../../lib/constants/endOfBrowserAsyncQueue.constants";
import { Button } from "../../../_base/_core/Button/Button.emotion";
import { Text } from "../../../_base/_core/Text/Text.emotion";
import type { TextInputProps } from "../TextInput/TextInput.emotion";
import { TextInput } from "../TextInput/TextInput.emotion";

const containerStyle: Interpolation = {
  display: "flex",
  placeContent: "center",
  placeItems: "center",
  position: "relative",
  width: spacing.full,
};
const dropdownStyle: Interpolation<ThemeType> = (theme) => ({
  background: theme.semanticColors.background,
  borderBottomLeftRadius: radius.ms,
  borderBottomRightRadius: radius.ms,
  borderBottomWidth: stroke.regular,
  borderColor: theme.semanticColors.neutralsLowest,
  borderLeftWidth: stroke.regular,
  borderRightWidth: stroke.regular,
  borderStyle: "solid",
  borderTopLeftRadius: radius.none,
  borderTopRightRadius: radius.none,
  borderTopWidth: stroke.none,
  display: "flex",
  flexDirection: "column",
  maxHeight: `${numericViewportHeight[50]} !important`,
  overflowX: "hidden",
  overflowY: "auto",
  padding: `${spacing.xs} ${spacing.ms}`,
  position: "absolute",
  rowGap: spacing.none,
  transform: "scaleY(0)",
  transformOrigin: "center top",
  transition: "transform 100ms ease-in-out",
  willChange: "transform",
  zIndex: zIndices.modalContent,
});
const hiddenLabelStyle: Interpolation = {
  opacity: 0,
};
const inputStyle: Interpolation<ThemeType> = (theme) => ({
  borderColor: theme.semanticColors.neutralsLowest,
  fontSize: fontSizes.bodyL,
  lineHeight: lineHeights.bodyL,
  transition: "border-radius 0ms 100ms",
  zIndex: zIndices[1],
  ":hover:not(:disabled):not(:focus)": {
    borderColor: theme.semanticColors.neutralsLowest,
  },
  ":focus": {
    borderColor: theme.semanticColors.neutralsLowest,
  },
});
const openDropdownStyle: Interpolation = {
  transform: "scaleY(1)",
};
const openInputStyle: Interpolation = {
  borderBottomLeftRadius: radius.none,
  borderBottomRightRadius: radius.none,
  transition: "none",
};
const optionStyle: Interpolation = {
  borderRadius: radius.ms,
  width: spacing.full,
};
const overridesStyle: Interpolation<{
  width: number;
  left?: number;
  top?: number;
}> = (props) => ({
  // left: `${props.left}px`,
  maxWidth: numericViewportWidth[100],
  // top: `${props.top}px`,
  width: `${props.width}px`,
});
const valueStyle: Interpolation = {
  alignItems: "center",
  display: "flex",
  height: spacing.full,
  justifyContent: "flex-start",
  left: numericPercentages[50],
  maxWidth: spacing.maxWidthWithPaddingXS,
  paddingLeft: `calc(${spacing.ms} + ${spacing.xxl})`,
  position: "absolute",
  textAlign: "left",
  top: spacing.none,
  transform: "translateX(-50%)",
  width: spacing.full,
  zIndex: zIndices[1],
};

export type SelectOption = {
  value: string | number;
  text?: string;
  label?: ReactNode;
};

export type ComboboxProps<T extends SelectOption> = Pick<
  TextInputProps,
  "placeholder" | "startIcon" | "isPersistentLabel" | "className"
> & {
  inputValue?: string;
  onInput?: (newInputValue: string) => void;
  value?: T;
  onChange?: (newValue: T) => void;
  options: T[];
};

const PopperPortal = ({ children }: PropsWithChildren) => {
  const [isClient, setIsClient] = useState<boolean>(false);
  const { themeContainerRef } = useTheme();

  useEffect(() => {
    if (typeof window !== "undefined") setIsClient(true);
  }, []);

  if (!isClient) return null;

  return createPortal(children, themeContainerRef.current);
};

export const Combobox = <T extends SelectOption>({
  startIcon,
  inputValue,
  onInput,
  value,
  onChange,
  options,
  placeholder,
  isPersistentLabel = false,
  ...props
}: ComboboxProps<T>) => {
  const [typed, setTyped] = useState<string | null>(null);
  const [selected, setSelected] = useState<T | null>(null);
  const [open, setOpen] = useState<boolean>(false);
  const [localOptions, setLocalOptions] = useState<T[] | null>(null);
  const [boxWidth, setBoxWidth] = useState<number>(200);
  const [boxLeft, setBoxLeft] = useState<number>(0);
  const [boxTop, setBoxTop] = useState<number>(0);
  const lastSearch = useRef<string>(typed ?? inputValue);
  const inputRef = useRef<HTMLInputElement>(null);
  const labelRef = useRef<HTMLLabelElement>(null);
  const listRefs = useRef<RefObject<HTMLButtonElement | null>[]>([]);
  const blurTimeout = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const numRefs = localOptions?.length ?? options?.length ?? 0;
    if (numRefs < listRefs.current.length) {
      listRefs.current = listRefs.current.slice(0, numRefs);
    } else if (numRefs > listRefs.current.length) {
      for (let i = listRefs.current.length; i < numRefs; i++) {
        listRefs.current.push(createRef<HTMLButtonElement>());
      }
    }
  }, [options, localOptions]);

  const establishSize = () => {
    if (labelRef.current) {
      const labelRect = labelRef.current.getBoundingClientRect();
      setBoxWidth(labelRect.width);
      setBoxLeft(labelRect.left);
      setBoxTop(labelRect.bottom); // put it below the input
    }
  };

  useEffect(() => {
    establishSize();

    if (typeof window !== "undefined")
      window.addEventListener("resize", establishSize);

    return () => {
      if (typeof window !== "undefined")
        window.removeEventListener("resize", establishSize);
    };
  }, []);

  const handleInput: FormEventHandler<HTMLInputElement> = (e) => {
    if (onInput) {
      onInput(e.currentTarget.value);
      setTyped(null);
      setLocalOptions(null);
    } else {
      setTyped(e.currentTarget.value);
      setLocalOptions(
        options.filter(
          (opt) =>
            opt.value
              .toString()
              .toLowerCase()
              .includes(e.currentTarget.value.toLowerCase()) ||
            opt.text
              ?.toLowerCase()
              ?.includes(e.currentTarget.value.toLowerCase()),
        ),
      );
    }
    if (!open) {
      setOpen(true);
      setTimeout(() => {
        inputRef.current?.focus();
      }, END_OF_BROWSER_ASYNC_QUEUE);
    }
  };

  const handleSelect = (selected: T) => {
    lastSearch.current = typed ?? inputValue;
    if (onInput) {
      onInput(null);
    }
    setTyped(null);

    if (onChange) {
      onChange(selected);
      setSelected(null);
    } else {
      setSelected(selected);
    }
    setOpen(false);
  };

  const handleReopen = () => {
    if (onChange) {
      onChange(null);
    } else {
      setSelected(null);
    }
    if (onInput) {
      onInput(lastSearch.current ?? "");
    } else {
      setTyped(lastSearch.current ?? "");
    }
    setOpen(true);

    setTimeout(() => {
      inputRef.current?.focus();
    }, END_OF_BROWSER_ASYNC_QUEUE);
  };

  const haltEvent = (e: KeyboardEvent) => {
    e.stopPropagation();
    e.preventDefault();
  };

  const handleKeyDownInput: KeyboardEventHandler<HTMLInputElement> = (e) => {
    switch (e.key) {
      case "Esc":
      case "Escape":
        if (open) {
          haltEvent(e);
          setOpen(false);
        }
        break;
      case "Enter":
      case "Return":
        if (!open) {
          haltEvent(e);
          setOpen(true);
        }
        break;
      case "ArrowDown":
      case "Down":
        haltEvent(e);
        if (!open) setOpen(true);
        else listRefs.current?.[0]?.current?.focus();
        break;
    }
  };

  const handleKeyDownOption = (
    option: T,
    e: KeyboardEvent<HTMLButtonElement>,
  ) => {
    const idx = parseInt(e.currentTarget.dataset.idx);
    switch (e.key) {
      case "Esc":
      case "Escape":
        haltEvent(e);
        setOpen(false);
        inputRef.current?.focus();
        break;
      case "Enter":
      case "Return":
      case "Space":
      case " ":
        haltEvent(e);
        handleSelect(option);
        break;
      case "ArrowDown":
      case "Down":
        haltEvent(e);
        if (listRefs.current?.[idx + 1]?.current) {
          listRefs.current[idx + 1].current.focus();
        } else {
          inputRef.current.focus();
        }
        break;
      case "ArrowUp":
      case "Up":
        haltEvent(e);
        if (listRefs.current?.[idx - 1]?.current) {
          listRefs.current[idx - 1].current.focus();
        } else {
          inputRef.current.focus();
        }
        break;
    }
  };

  const handleBlur = () => {
    blurTimeout.current = setTimeout(() => {
      setOpen(false);
    }, 100);
  };

  const handleFocus = () => {
    clearTimeout(blurTimeout.current);
  };

  // unfortunately, neither Radix's Select nor Radix's Dropdown cooperate with becoming a Combobox, and
  // despite having "Combobox" on their to-do list for almost 3 years (as of this writing), Radix has yet
  // to implement an actual combobox. So we had to do this custom instead.

  return (
    <Popper.Root>
      <div css={containerStyle} onBlur={handleBlur} onFocus={handleFocus}>
        <Popper.Anchor asChild>
          <TextInput
            placeholder={placeholder}
            placeholderCSS={!!(selected ?? value) ? hiddenLabelStyle : {}}
            value={typed ?? inputValue ?? ""}
            onInput={handleInput}
            onKeyDown={handleKeyDownInput}
            startIcon={startIcon}
            isPersistentLabel={isPersistentLabel && !!placeholder}
            css={[inputStyle, open && openInputStyle]}
            ref={inputRef}
            labelRef={labelRef}
            {...props}
          />
        </Popper.Anchor>
        {!!(selected ?? value) && !open && (
          <Text
            as={"div"}
            type={"body"}
            size={"l"}
            css={valueStyle}
            onClick={handleReopen}
          >
            {(selected ?? value).label ??
              (selected ?? value).text ??
              (selected ?? value).value.toString()}
          </Text>
        )}
      </div>
      <PopperPortal>
        <Popper.Content
          side={"bottom"}
          align={"start"}
          sticky={"always"}
          avoidCollisions={false}
          css={[
            dropdownStyle,
            overridesStyle({ left: boxLeft, top: boxTop, width: boxWidth }),
            open && openDropdownStyle,
          ]}
          onFocus={handleFocus}
        >
          {(localOptions ?? options).map((opt, idx) => (
            <Button
              key={opt.value}
              variant={"transparentPrimary"}
              data-idx={idx}
              onKeyDown={handleKeyDownOption.bind(this, opt)}
              onClick={() => handleSelect(opt)}
              size={"l"}
              css={optionStyle}
              ref={listRefs.current?.[idx] ?? null}
            >
              {opt.label ?? opt.value.toString()}
            </Button>
          ))}
        </Popper.Content>
      </PopperPortal>
    </Popper.Root>
  );
};
